import React, { useEffect, useMemo, useState } from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { Link, useHistory, useLocation } from "react-router-dom";
import apiService from "../services/api.service";
import { ROLE_LABELS } from "../utils/roles";
import { EditIcon, DeleteIcon } from "../Components/Icons/ActionIcons";
import UserEditModal from "../Components/UserEditModal";
import FlashAlert from "../Components/FlashAlert/FlashAlert";
import "./userList.css";

const FILTERS = {
  ALL: "all",
  VERIFIED: "verified",
  UNVERIFIED: "unverified",
};

const extractCount = (value) => {
  if (value == null) return 0;
  if (typeof value === "number") return value;
  if (typeof value.count === "number") return value.count;
  if (Array.isArray(value)) return value[0] ?? 0;
  if (typeof value[0] === "number") return value[0];
  return 0;
};

const isEmailVerified = (user) => {
  const value = user?.is_verified ?? user?.isVerified;
  return value === true || value === 1 || value === "1" || value === "true";
};

const verifiedParamForFilter = (filter) => {
  if (filter === FILTERS.VERIFIED) return true;
  if (filter === FILTERS.UNVERIFIED) return false;
  return undefined;
};

const UserList = ({ embedded = false }) => {
  const [users, setUsers] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(FILTERS.ALL);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [roles, setRoles] = useState({});
  const [userToEdit, setUserToEdit] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    if (!embedded) return;
    const editId = new URLSearchParams(location.search).get("edit");
    if (editId) {
      setUserToEdit({ id: editId });
      history.replace("/dashboard/utenti");
    }
  }, [embedded, location.search, history]);

  useEffect(() => {
    const loadStats = async () => {
      try {
        const response = await apiService.getStats();
        setStats(response);
      } catch (error) {
        console.error(error);
      }
    };

    loadStats();
  }, []);

  useEffect(() => {
    const loadUsers = async () => {
      setLoading(true);
      try {
        const userList = await apiService.getUsers(verifiedParamForFilter(filter));
        setUsers(userList);
        const rolesData = {};
        await Promise.all(
          (userList.data || []).map(async (user) => {
            try {
              const roleResponse = await apiService.getUserRole(user.id);
              rolesData[user.id] = roleResponse.data.role || "Partecipante";
            } catch {
              rolesData[user.id] = "Partecipante";
            }
          })
        );
        setRoles(rolesData);
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, [filter]);

  const userData = useMemo(() => users?.data ?? [], [users]);

  const counts = useMemo(() => {
    const totalFromStats = extractCount(stats?.data?.total);
    const verifiedFromStats = extractCount(stats?.data?.verified);
    const totalFromProvinces =
      stats?.data?.province?.reduce(
        (sum, row) => sum + (row.count ?? row.count_1 ?? row[0] ?? 0),
        0
      ) ?? 0;

    const all = totalFromStats || totalFromProvinces || (
      filter === FILTERS.ALL ? userData.length : 0
    );
    const verified =
      verifiedFromStats ||
      (filter === FILTERS.VERIFIED ? userData.length : userData.filter(isEmailVerified).length);

    return {
      all,
      verified,
      unverified: Math.max(all - verified, 0),
    };
  }, [stats, userData, filter]);

  const filteredUsers = userData;

  const showVerifiedStatus = (user) => {
    if (filter === FILTERS.VERIFIED) return true;
    if (filter === FILTERS.UNVERIFIED) return false;
    return isEmailVerified(user);
  };

  const filterOptions = [
    { key: FILTERS.ALL, label: "Utenti totali", count: counts.all },
    { key: FILTERS.VERIFIED, label: "Email verificata", count: counts.verified },
    { key: FILTERS.UNVERIFIED, label: "Email non verificata", count: counts.unverified },
  ];

  const openEditModal = (user) => {
    setSuccessMessage("");
    setUserToEdit(user);
  };

  const closeEditModal = () => {
    setUserToEdit(null);
  };

  const handleUserSaved = async (updatedUser) => {
    setUsers((prev) => ({
      ...prev,
      data: (prev?.data || []).map((u) => (u.id === updatedUser.id ? { ...u, ...updatedUser } : u)),
    }));
    try {
      const roleResponse = await apiService.getUserRole(updatedUser.id);
      setRoles((prev) => ({
        ...prev,
        [updatedUser.id]: roleResponse.data.role || "Partecipante",
      }));
    } catch {
      /* keep existing event role */
    }
    setSuccessMessage("Modifica utente avvenuta con successo.");
  };

  const openDeleteModal = (user) => {
    setDeleteError("");
    setUserToDelete(user);
  };

  const closeDeleteModal = () => {
    if (!deleting) {
      setUserToDelete(null);
      setDeleteError("");
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    setDeleting(true);
    setDeleteError("");

    try {
      await apiService.deleteUser(userToDelete.id);
      setUsers((prev) => ({
        ...prev,
        data: prev.data.filter((u) => u.id !== userToDelete.id),
      }));
      setStats((prev) => {
        if (!prev?.data) return prev;
        const total = Math.max(extractCount(prev.data.total) - 1, 0);
        const verified = isEmailVerified(userToDelete)
          ? Math.max(extractCount(prev.data.verified) - 1, 0)
          : extractCount(prev.data.verified);
        return {
          ...prev,
          data: {
            ...prev.data,
            total,
            verified,
          },
        };
      });
      setUserToDelete(null);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail === "cannot delete own account") {
        setDeleteError("Non puoi eliminare il tuo account.");
      } else if (detail === "cannot delete admin user") {
        setDeleteError("Non è possibile eliminare un utente amministratore.");
      } else {
        setDeleteError("Eliminazione non riuscita. Riprova o contatta infoceformedsrl@gmail.com");
      }
    } finally {
      setDeleting(false);
    }
  };

  const content = (
    <>
      <div className="users-card">
        <div className="users-header">
          <h1 className="users-title">Lista Utenti</h1>
          <div className="events-header-actions">
            {!loading && (
              <span className="users-count">
                {filteredUsers.length} di {counts.all}{" "}
                {counts.all === 1 ? "registrato" : "registrati"}
              </span>
            )}
            <Button
              variant="primary"
              className="users-edit-btn"
              onClick={() => history.push(embedded ? "/dashboard/utenti/nuovo" : "/users/new", { isAdmin: true })}
            >
              Nuovo utente
            </Button>
          </div>
        </div>

        {successMessage && (
          <FlashAlert variant="success" onHidden={() => setSuccessMessage("")}>
            {successMessage}
          </FlashAlert>
        )}

        {!loading && counts.all > 0 && (
          <div className="users-filters" role="group" aria-label="Filtra per stato email">
            {filterOptions.map((option) => (
              <button
                key={option.key}
                type="button"
                className={`users-filter-btn${filter === option.key ? " users-filter-btn--active" : ""}`}
                onClick={() => setFilter(option.key)}
                aria-pressed={filter === option.key}
              >
                {option.label}
                <span className="users-filter-count">{option.count}</span>
              </button>
            ))}
          </div>
        )}

        {loading && <p className="users-loading">Caricamento utenti...</p>}

        {!loading && counts.all === 0 && (
          <Alert variant="info">Non ci sono utenti registrati</Alert>
        )}

        {!loading && counts.all > 0 && filteredUsers.length === 0 && (
          <Alert variant="info">
            Nessun utente corrisponde al filtro selezionato.
          </Alert>
        )}

        {!loading && filteredUsers.length > 0 && (
          <div className="users-table">
            <div className="users-table-head">
              <span>Nome</span>
              <span>Cognome</span>
              <span>Email</span>
              <span>Ruolo piattaforma</span>
              <span>Ruolo evento</span>
              <span>Stato email</span>
              <span aria-hidden="true" />
            </div>

            {filteredUsers.map((user) => (
              <div className="users-table-row" key={user.id}>
                <div className="users-field">
                  <span className="users-field-label">Nome</span>
                  <p className="users-field-value">{user.first_name}</p>
                </div>
                <div className="users-field">
                  <span className="users-field-label">Cognome</span>
                  <p className="users-field-value">{user.last_name}</p>
                </div>
                <div className="users-field">
                  <span className="users-field-label">Email</span>
                  <p className="users-field-value users-field-value--email">
                    {user.username}
                  </p>
                </div>
                <div className="users-field">
                  <span className="users-field-label">Ruolo piattaforma</span>
                  <p className="users-field-value">
                    {ROLE_LABELS[user.platform_role] || user.platform_role || "Utente base"}
                  </p>
                </div>
                <div className="users-field">
                  <span className="users-field-label">Ruolo evento</span>
                  <p className="users-field-value">{roles[user.id] || "Partecipante"}</p>
                </div>
                <div className="users-field">
                  <span className="users-field-label">Stato email</span>
                  <span
                    className={`users-status-badge${showVerifiedStatus(user) ? " users-status-badge--verified" : " users-status-badge--unverified"}`}
                  >
                    {showVerifiedStatus(user) ? "Verificata" : "Non verificata"}
                  </span>
                </div>
                <div className="users-actions">
                  {embedded ? (
                    <Button
                      type="button"
                      variant="primary"
                      className="users-edit-btn users-icon-btn"
                      title="Modifica utente"
                      aria-label="Modifica utente"
                      onClick={() => openEditModal(user)}
                    >
                      <EditIcon />
                    </Button>
                  ) : (
                    <Button
                      as={Link}
                      to={`/users/${user.id}/edit`}
                      variant="primary"
                      className="users-edit-btn users-icon-btn"
                      title="Modifica utente"
                      aria-label="Modifica utente"
                    >
                      <EditIcon />
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="primary"
                    className="users-edit-btn users-icon-btn users-icon-btn--danger"
                    onClick={() => openDeleteModal(user)}
                    title="Elimina utente"
                    aria-label="Elimina utente"
                  >
                    <DeleteIcon />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <UserEditModal
        show={!!userToEdit}
        userId={userToEdit?.id}
        onHide={closeEditModal}
        onSaved={handleUserSaved}
      />

      <Modal show={!!userToDelete} onHide={closeDeleteModal} centered>
        <Modal.Header closeButton={!deleting}>
          <Modal.Title>Elimina utente</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteError && (
            <FlashAlert variant="danger" onHidden={() => setDeleteError("")}>
              {deleteError}
            </FlashAlert>
          )}
          <p>
            Sei sicuro di voler eliminare{" "}
            <strong>
              {userToDelete?.first_name} {userToDelete?.last_name}
            </strong>{" "}
            ({userToDelete?.username})?
          </p>
          <p className="users-delete-warning">
            L&apos;operazione è irreversibile e rimuoverà anche le presenze
            registrate per questo utente.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-primary" onClick={closeDeleteModal} disabled={deleting}>
            Annulla
          </Button>
          <Button
            variant="primary"
            className="users-edit-btn"
            onClick={handleDeleteUser}
            disabled={deleting}
          >
            {deleting ? "Eliminazione..." : "Elimina definitivamente"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );

  if (embedded) {
    return content;
  }

  return <div className="users-page">{content}</div>;
};

export default UserList;
